import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  ArrowSquareOut, CheckCircle, CopySimple, GoogleChromeLogo, WarningCircle,
} from "@phosphor-icons/react";

const CHROME_PLAY_URL = "https://play.google.com/store/apps/details?id=com.android.chrome";

// Chrome handoff for unsupported Android browsers. The unconsumed pairing token
// lives in component memory ONLY — never rendered as text, never stored, never logged.
export function ContinueInChrome({ token }) {
  const [gone, setGone] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  // Poll pairing status: once Chrome redeems the token (or the 5-minute link
  // expires), this page becomes unusable.
  useEffect(() => {
    if (!token) return undefined;
    const iv = setInterval(async () => {
      try {
        const r = await api.post("/devices/pair/check", { token });
        if (r.data?.status !== "pending") {
          setGone(true);
          clearInterval(iv);
        }
      } catch (_) { /* keep polling */ }
    }, 4000);
    return () => { clearInterval(iv); clearTimeout(timerRef.current); };
  }, [token]);

  const continueInChrome = useCallback(() => {
    if (!token) return;
    // User-initiated Android intent targeting the installed Chrome package with
    // the exact same secure pairing URL. Never fired automatically.
    const intentUrl = `intent://${window.location.host}/api/devices/pair/${encodeURIComponent(token)}` +
      "#Intent;scheme=https;package=com.android.chrome;end";
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      // Still visible after the tap => the intent could not be resolved (no Chrome).
      if (document.visibilityState === "visible") setShowFallback(true);
    }, 2500);
    window.location.href = intentUrl;
  }, [token]);

  const copyLink = useCallback(async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/api/devices/pair/${encodeURIComponent(token)}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* clipboard unavailable */ }
  }, [token]);

  if (gone || !token) {
    return (
      <div data-testid="handoff-complete" className="text-center">
        <WarningCircle size={40} weight="duotone" className="text-amber-600 mx-auto mt-5" />
        <h1 className="mt-4 font-display font-extrabold tracking-tighter text-2xl" data-testid="handoff-complete-title">
          This page can no longer be used
        </h1>
        <p className="mt-3 t-support text-muted-foreground text-left">
          {gone
            ? "Setup has continued in Google Chrome, or this five-minute secure link has expired. If installation did not start in Chrome, generate a new QR code from Get Mobile Access on your signed-in device."
            : "This secure setup link could not be carried over. Generate a new QR code from Get Mobile Access on your signed-in device."}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="chrome-handoff-step" className="text-center">
      <GoogleChromeLogo size={40} weight="duotone" className="text-primary mx-auto mt-5" />
      <h1 className="mt-4 font-display font-extrabold tracking-tighter text-2xl" data-testid="chrome-handoff-title">
        Chrome is required to install Rotary GOV Report Builder
      </h1>
      <p className="mt-3 t-support text-muted-foreground text-left" data-testid="chrome-handoff-message">
        Continue this secure setup in Google Chrome. You will not need to scan the
        QR code again.
      </p>
      <button
        data-testid="continue-in-chrome-button" type="button" onClick={continueInChrome}
        className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-4 text-sm font-semibold uppercase tracking-[0.15em] bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
      >
        <GoogleChromeLogo size={18} weight="bold" />
        Continue in Chrome
      </button>

      {showFallback && (
        <div data-testid="chrome-handoff-fallback" className="mt-6 text-left border-t border-border pt-5">
          <p className="t-support text-muted-foreground">
            Chrome doesn&apos;t seem to be installed on this phone:
          </p>
          <a
            data-testid="install-chrome-button" href={CHROME_PLAY_URL} target="_blank" rel="noopener noreferrer"
            className="mt-4 w-full flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] border border-primary text-primary hover:bg-primary/5 transition-colors"
          >
            <ArrowSquareOut size={18} weight="bold" />
            Install Google Chrome
          </a>
          <button
            data-testid="copy-setup-link-button" type="button" onClick={copyLink}
            className="mt-3 w-full flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.15em] border border-border text-foreground hover:bg-muted transition-colors"
          >
            {copied ? <CheckCircle size={18} weight="bold" className="text-green-600" /> : <CopySimple size={18} weight="bold" />}
            {copied ? "Link copied" : "Copy secure setup link"}
          </button>
          <p className="mt-3 t-support text-muted-foreground">
            After installing Chrome, open Chrome and paste the copied link.
          </p>
        </div>
      )}
    </div>
  );
}
